import _ from 'lodash';

/**
 * Checks if `value` is not `null` or `undefined`.
 *
 * @example
 * _.isNotNil(null);      // => false
 * _.isNotNil(undefined); // => false
 * _.isNotNil('aaaa');    // => true
 * _.isNotNil(0);         // => true
 */
export const isNotNil = <T>(value: T | null | undefined): value is T =>
  !_.isNil(value);

/**
 * Checks if `value` is not `empty`.
 *
 * @example
 * _.isNotEmpty(null);  // => false
 * _.isNotEmpty('');    // => false
 * _.isNotEmpty(0);     // => false
 * _.isNotEmpty({});    // => false
 * _.isNotEmpty([]);    // => false
 * _.isNotEmpty('aaaa');  // => true
 */
export const isNotEmpty = <T>(value: T | null | undefined): value is T =>
  !_.isEmpty(value);

/**
 * Removes null and undefined properties from object
 * @example
 * _.compactObject({ a: 1, c: null });      // => { a: 1 }
 * _.compactObject({ b: 1, d: undefined }); // => { b: 1 }
 */
export const compactObject = <T extends object>(obj: T) => _.pickBy(obj, isNotNil);

/**
 * Extends lodash's memoize function
 * to be able to memoize functions with several arguments.
 * (the standard memoize function can only memoize functions with one argument)
 *
 *  !!!Attention: Use only primitive types for input parameters (number, string, boolean...)
 *              function most probably will not work with object parameters!!! like "function test(val: object)"
 * @example
 * const addMemoized = memoizeEx((a, b, c) => a + b + c);
 * addMemoized(1, 2, 3); // => 6
 */
export const memoizeEx = <T extends (...args: any) => any>(
  fn: Parameters<typeof _.memoize<T>>[0],
) => _.memoize<T>(fn, (...v) => v.map(String).join(','));
